import { useAudioPlayer, useAudioPlayerStatus } from 'expo-audio';
import { Pause, Play, Volume2 } from 'lucide-react-native';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import {
  type AccessibilityActionEvent,
  type GestureResponderEvent,
  type LayoutChangeEvent,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { Caption } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';

export interface AudioPlayerHandle {
  seekTo: (seconds: number) => void;
  play: () => void;
  pause: () => void;
}

interface AudioPlayerProps {
  url: string;
  durationSeconds?: number | null;
  testID?: string;
}

const WAVEFORM_BARS = 32;
const SEEK_STEP_SECONDS = 5;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function pseudoRandomBars(seed: number): number[] {
  const bars: number[] = [];
  let s = seed;
  for (let i = 0; i < WAVEFORM_BARS; i++) {
    s = (s * 9301 + 49297) % 233280;
    const v = 0.3 + (s / 233280) * 0.7;
    bars.push(v);
  }
  return bars;
}

export const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(
  ({ url, durationSeconds, testID }, ref) => {
    const { t } = useTranslation();
    const player = useAudioPlayer({ uri: url });
    const status = useAudioPlayerStatus(player);
    const waveformWidthRef = useRef(0);

    const total = status.duration > 0 ? status.duration : (durationSeconds ?? 0);
    const current = status.currentTime;
    const progress = total > 0 ? Math.min(current / total, 1) : 0;
    const isPlaying = status.playing;
    const bars = useMemo(() => pseudoRandomBars(url.length), [url]);

    const seek = useCallback(
      (seconds: number) => {
        const clamped = Math.max(0, Math.min(seconds, total > 0 ? total : seconds));
        player.seekTo(clamped).catch((err) => {
          console.warn('AudioPlayer seek failed', err);
        });
      },
      [player, total],
    );

    useImperativeHandle(
      ref,
      () => ({
        seekTo: (seconds: number) => {
          seek(seconds);
          if (!status.playing) player.play();
        },
        play: () => player.play(),
        pause: () => player.pause(),
      }),
      [player, status.playing, seek],
    );

    const togglePlay = () => {
      if (isPlaying) {
        player.pause();
      } else {
        player.play();
      }
    };

    const handleWaveformLayout = (e: LayoutChangeEvent) => {
      waveformWidthRef.current = e.nativeEvent.layout.width;
    };

    const handleWaveformPress = (e: GestureResponderEvent) => {
      const width = waveformWidthRef.current;
      if (width <= 0 || total <= 0) return;
      const x = e.nativeEvent.locationX;
      const frac = Math.max(0, Math.min(x / width, 1));
      seek(total * frac);
    };

    const handleAccessibilityAction = (e: AccessibilityActionEvent) => {
      switch (e.nativeEvent.actionName) {
        case 'increment':
          seek(current + SEEK_STEP_SECONDS);
          break;
        case 'decrement':
          seek(current - SEEK_STEP_SECONDS);
          break;
        case 'activate':
          togglePlay();
          break;
      }
    };

    return (
      <View
        style={styles.root}
        accessible
        accessibilityRole="adjustable"
        accessibilityLabel={`${t('voiceAgents.audio.playerLabel')}, ${isPlaying ? t('voiceAgents.audio.playing') : t('voiceAgents.audio.paused')}, ${t('voiceAgents.audio.position')} ${formatTime(current)} ${t('voiceAgents.audio.of')} ${formatTime(total)}`}
        accessibilityValue={{
          min: 0,
          max: Math.max(1, Math.floor(total)),
          now: Math.floor(current),
        }}
        accessibilityActions={[
          {
            name: 'increment',
            label: `${t('voiceAgents.audio.forward')} ${SEEK_STEP_SECONDS} ${t('voiceAgents.audio.seconds')}`,
          },
          {
            name: 'decrement',
            label: `${t('voiceAgents.audio.rewind')} ${SEEK_STEP_SECONDS} ${t('voiceAgents.audio.seconds')}`,
          },
          {
            name: 'activate',
            label: isPlaying ? t('voiceAgents.audio.pause') : t('voiceAgents.audio.play'),
          },
        ]}
        onAccessibilityAction={handleAccessibilityAction}
        testID={testID}
      >
        <View style={styles.row}>
          <Pressable
            onPress={togglePlay}
            style={({ pressed }) => [styles.playButton, pressed && styles.pressed]}
            accessibilityRole="button"
            accessibilityLabel={
              isPlaying ? t('voiceAgents.audio.pauseAudio') : t('voiceAgents.audio.playAudio')
            }
            accessibilityState={{ busy: !status.isLoaded }}
            hitSlop={8}
          >
            {isPlaying ? (
              <Pause size={20} color={colors.text.primary} />
            ) : (
              <Play size={20} color={colors.text.primary} />
            )}
          </Pressable>

          <View style={styles.waveformContainer}>
            <Pressable
              onPress={handleWaveformPress}
              onLayout={handleWaveformLayout}
              style={styles.waveformRow}
              hitSlop={{ top: 8, bottom: 8 }}
              accessibilityElementsHidden
              importantForAccessibility="no"
            >
              {bars.map((h, i) => {
                const filled = i / WAVEFORM_BARS <= progress;
                return (
                  <View
                    // biome-ignore lint/suspicious/noArrayIndexKey: static waveform bars
                    key={`bar-${i}`}
                    style={[
                      styles.bar,
                      {
                        height: 4 + h * 22,
                        backgroundColor: filled ? colors.primary.main : colors.border.subtle,
                      },
                    ]}
                  />
                );
              })}
            </Pressable>
            <View style={styles.timeRow}>
              <Caption style={styles.time}>{formatTime(current)}</Caption>
              <Caption style={styles.time}>{formatTime(total)}</Caption>
            </View>
          </View>

          <View
            style={styles.volumeIcon}
            accessibilityElementsHidden
            importantForAccessibility="no"
          >
            <Volume2 size={16} color={colors.text.tertiary} />
          </View>
        </View>
      </View>
    );
  },
);

AudioPlayer.displayName = 'AudioPlayer';

const styles = StyleSheet.create({
  root: {
    backgroundColor: colors.background.elevated,
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  playButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary.main,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: {
    opacity: 0.7,
  },
  waveformContainer: {
    flex: 1,
    gap: 4,
  },
  waveformRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    gap: 2,
  },
  bar: {
    flex: 1,
    minWidth: 2,
    borderRadius: 2,
  },
  timeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  time: {
    color: colors.text.tertiary,
    fontSize: 11,
    fontVariant: ['tabular-nums'],
  },
  volumeIcon: {
    paddingLeft: 4,
  },
});
